import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import Head from 'next/head';
import qs from 'querystring';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Pagination from '@/components/ui/Pagination/Pagination';
import { getItemsPerPage } from '@/constants/pagination';
import { ESearchFilters } from '@/constants/searchFilters';
import { useRouter } from '@/hooks/useRouter';
import { applySearchFilter } from '@/redux/modules/search';
import { lastPaginationDelta } from '@/redux/selectors/analytics-selectors/search/searchViewedEventData';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { getSearchResults as getSearchResultsSelector } from '@/redux/selectors/search/searchResults';
import { trackEvent } from '@/services/track-event';
import { clamp } from '@/utility/numbers';

const PaginationContainer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { total, startPosition, stopPosition } = useSelector(getSearchResultsMeta);

  const { [ESearchFilters.PAGE_LIMIT]: pageLimit } = useSelector(getQueryParams);
  const { nearby } = useSelector(getSearchResultsSelector);

  const itemsPerPage = getItemsPerPage(pageLimit);
  const router = useRouter();

  // Normally having a prop as a state is bad practice.
  // In this case, it acts as a local holder of pagination data
  // so the app appears responsive while it fetches new data.
  // The illusion of responsiveness.
  const [localStop, setLocalStop] = useState(stopPosition || itemsPerPage);
  const [localStart, setLocalStart] = useState(startPosition || 1);

  const totalPages = Math.ceil(total / itemsPerPage);
  const currentPage = Math.ceil(localStop / itemsPerPage);

  // Handle reset from the store and override current "local" page selection.
  useEffect(() => {
    if (startPosition === 1) {
      setLocalStart(startPosition);
    }
    if (stopPosition <= itemsPerPage) {
      setLocalStop(stopPosition);
    }
  }, [itemsPerPage, startPosition, stopPosition]);

  // Handle if the page is loaded by url which already contains offset
  useEffect(() => {
    if (localStart === 1 && startPosition > 1 && localStop === 0 && stopPosition > itemsPerPage) {
      setLocalStart(startPosition);
      setLocalStop(stopPosition);
    }
  }, [itemsPerPage, startPosition, stopPosition, localStop, localStart]);

  const handlePaginationChange = useCallback(
    (page: number) => {
      if (!(total && stopPosition)) {
        return;
      }

      const clampedPage = clamp(page, 1, totalPages);
      const offset = (clampedPage - 1) * itemsPerPage; // Use clampedPage for consistency

      const newStart = (clampedPage - 1) * itemsPerPage + 1;
      const newStop = Math.min(clampedPage * itemsPerPage, total);

      // Calculate pagination delta for analytics
      const currentPageNumber = Math.ceil(localStart / itemsPerPage);
      const deltaPage = clampedPage - currentPageNumber;
      lastPaginationDelta(deltaPage);

      // Capture "before" state before updating
      const beforeOffset = localStart - 1;

      // Only update local state if we need to show intermediate state
      setLocalStart(newStart);
      setLocalStop(newStop);

      trackEvent({
        event: 'search/page',
        action: 'apply',
        before: { 'page[offset]': beforeOffset },
        after: { 'page[offset]': offset },
      });

      // Scroll to top for better UX
      if (typeof window === 'object') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      // Perform search via store
      dispatch(
        applySearchFilter({
          [ESearchFilters.PAGE_OFFSET]: offset.toString(),
          [ESearchFilters.PAGE_LIMIT]: itemsPerPage.toString(),
        }),
      );
    },
    [total, stopPosition, itemsPerPage, totalPages, localStart, dispatch],
  );

  const [relPrev, relNext] = useMemo(() => {
    let prevUrl, nextUrl;

    const origin =
      (typeof window === 'object' && window.location.origin) || 'https://www.outdoorsy.com';
    const { pathname, query } = router;
    const currentPageOffset =
      Number(query[ESearchFilters.PAGE_OFFSET]) || Math.max(currentPage - 1, 0) * itemsPerPage;

    if (currentPage > 1) {
      const prevQuery = {
        ...query,
        [ESearchFilters.PAGE_OFFSET]: `${currentPageOffset - itemsPerPage}`,
      };
      prevUrl = `${origin}${pathname}?${qs.stringify(prevQuery)}`;
    }

    if (currentPage < totalPages) {
      const nextQuery = {
        ...query,
        [ESearchFilters.PAGE_OFFSET]: `${currentPageOffset + itemsPerPage}`,
      };
      nextUrl = `${origin}${pathname}?${qs.stringify(nextQuery)}`;
    }

    return [prevUrl, nextUrl];
  }, [router, currentPage, totalPages, itemsPerPage]);

  if (nearby && startPosition === 1) {
    return null;
  }

  if (total && stopPosition) {
    return (
      <>
        <Head>
          {relPrev && <link rel="prev" href={relPrev} data-testid="search-link-rel-prev" />}
          {relNext && <link rel="next" href={relNext} data-testid="search-link-rel-next" />}
        </Head>
        <div className="flex flex-col md:flex-row md:justify-between pb-11 lg:pt-4 lg:pb-12">
          <Pagination
            className="self-center w-full mb-4 md:mb-0 md:w-auto"
            itemsPerPage={itemsPerPage}
            totalItems={total}
            stopPosition={localStop}
            onPaginationChange={handlePaginationChange}
            testIdPrefix="search"
          />
          <Text
            variant={ETextStyleVariant.SmallBold}
            className="text-center autoType200 md:flex md:flex-col md:justify-center"
            data-testid="search-results">
            {intl.formatMessage(
              {
                defaultMessage: '{start}-{stop} of {total} total results',
                id: 'b5htkF',
                description: 'Pagination start, stop, and total values.',
              },
              {
                start: localStart,
                stop: localStop,
                total,
              },
            )}
          </Text>
        </div>
      </>
    );
  }

  return null;
};

export default PaginationContainer;
